/**
 * Turning a best-effort screenshot read into something the funnel builder can
 * show (FFH-2738).
 *
 * The OCR pipeline identifies all fifteen players correctly in roughly 85% of
 * imports. The rest misclassify a position or miss a player, and a squad that
 * is not exactly 2 GK / 5 DEF / 5 MID / 3 FWD is refused by `/team-rating`.
 * Chasing individual misreads makes the parser more fragile rather than more
 * accurate, so the read is treated as a draft: whatever fits the squad is
 * placed on the builder pitch, whatever does not is reported, and the visitor
 * fixes the difference by hand.
 */

import type { components } from '../../types/api/generated';

/** One player the import endpoint matched, exactly as it sends it. */
export type ScreenshotMatch =
  components['schemas']['ImportTeamScreenshotResponse']['data']['team']['players'][number];

export type SquadPosition = 'GK' | 'DEF' | 'MID' | 'FWD';

export const SQUAD_POSITIONS: readonly SquadPosition[] = [
  'GK',
  'DEF',
  'MID',
  'FWD'
];

/** The only squad shape FPL allows, and the one `/team-rating` enforces. */
export const REQUIRED_BY_POSITION: Record<SquadPosition, number> = {
  GK: 2,
  DEF: 5,
  MID: 5,
  FWD: 3
};

/**
 * Which of the builder's fifteen slots belong to each position, starting XI
 * first then bench.
 *
 * The funnel pitch is a fixed 3-4-3 with a fixed bench, so a slot's position is
 * a property of the layout rather than of whoever is standing in it. Mirrors
 * `PITCH_XI_ROWS` and `PITCH_BENCH_ROW` in components/manual-team-entry: if
 * either changes shape, this has to change with it.
 */
export const BUILDER_SLOTS_BY_POSITION: Record<SquadPosition, number[]> = {
  GK: [0, 11],
  DEF: [1, 2, 3, 12, 14],
  MID: [4, 5, 6, 7, 13],
  FWD: [8, 9, 10]
};

export const FULL_SQUAD_SIZE = 15;

const POSITION_LABELS: Record<SquadPosition, { one: string; many: string }> = {
  GK: { one: 'goalkeeper', many: 'goalkeepers' },
  DEF: { one: 'defender', many: 'defenders' },
  MID: { one: 'midfielder', many: 'midfielders' },
  FWD: { one: 'forward', many: 'forwards' }
};

/** A position that still needs players, and how many. */
export type SquadShortfall = { position: SquadPosition; count: number };

export type ScreenshotSquadAssessment = {
  /** True when the read is a legal squad of fifteen distinct players. */
  isComplete: boolean;
  /** Builder slot index (0-14) to the match placed there, or null for a gap. */
  placements: Array<ScreenshotMatch | null>;
  /**
   * Reads with nowhere to go: surplus in a position the read over-filled, and
   * the second sighting of a player read twice. Shown to the visitor, because
   * a player silently vanishing from their own squad is the one outcome they
   * cannot act on.
   */
  dropped: ScreenshotMatch[];
};

/**
 * The same player read twice fills two slots and leaves the squad a player
 * short, so only the most confident sighting is kept. First-seen order is
 * preserved: the OCR returns players in pitch reading order, which is the
 * order the builder places them in.
 */
export const dedupeMatches = (
  matches: readonly ScreenshotMatch[]
): { unique: ScreenshotMatch[]; duplicates: ScreenshotMatch[] } => {
  const bestById = new Map<string, ScreenshotMatch>();
  const duplicates: ScreenshotMatch[] = [];

  for (const match of matches) {
    const existing = bestById.get(match.player.id);
    if (!existing) {
      bestById.set(match.player.id, match);
      continue;
    }
    if (match.confidence > existing.confidence) {
      bestById.set(match.player.id, match);
      duplicates.push(existing);
    } else {
      duplicates.push(match);
    }
  }

  const unique = matches.filter(
    (match) => bestById.get(match.player.id) === match
  );
  return { unique, duplicates };
};

/**
 * Which surplus reads to give up on when a position holds more players than a
 * squad allows.
 *
 * The least confident reads go, because a misclassified player is precisely the
 * one the parser was least sure of: in the reported 6 DEF / 4 MID case the
 * misread midfielder is the sixth defender, so dropping it both fixes the
 * defence and leaves the empty midfield slot it belongs in.
 */
const chooseSurplus = (
  candidates: readonly ScreenshotMatch[],
  surplusCount: number
): Set<ScreenshotMatch> =>
  new Set(
    [...candidates]
      .sort((one, two) => one.confidence - two.confidence)
      .slice(0, surplusCount)
  );

/**
 * Places a screenshot read onto the builder's fifteen slots, keeping what fits
 * and naming what does not.
 */
export const assessScreenshotSquad = (
  matches: readonly ScreenshotMatch[]
): ScreenshotSquadAssessment => {
  const { unique, duplicates } = dedupeMatches(matches);
  const placements: Array<ScreenshotMatch | null> = Array.from(
    { length: FULL_SQUAD_SIZE },
    () => null
  );
  const dropped: ScreenshotMatch[] = [...duplicates];

  for (const position of SQUAD_POSITIONS) {
    const candidates = unique.filter(
      (match) => match.player.position === position
    );
    const slots = BUILDER_SLOTS_BY_POSITION[position];
    const surplus = chooseSurplus(
      candidates,
      Math.max(0, candidates.length - slots.length)
    );

    let slotIndex = 0;
    for (const match of candidates) {
      if (surplus.has(match)) {
        dropped.push(match);
        continue;
      }
      placements[slots[slotIndex]] = match;
      slotIndex += 1;
    }
  }

  const isComplete =
    dropped.length === 0 && placements.every((match) => match !== null);

  return { isComplete, placements, dropped };
};

/** Minimal slot shape, so both funnels' team states can be measured. */
type FilledSlot = { playerId: number; player: object | null };

/**
 * What the pitch is still missing, read from the slots themselves rather than
 * from the original screenshot, so the line stays true as the visitor edits.
 */
export const countSlotShortfall = (
  team: readonly FilledSlot[]
): SquadShortfall[] => {
  const shortfall: SquadShortfall[] = [];

  for (const position of SQUAD_POSITIONS) {
    const count = BUILDER_SLOTS_BY_POSITION[position].filter(
      (slot) => !team.find((member) => member.playerId === slot)?.player
    ).length;
    if (count > 0) shortfall.push({ position, count });
  }

  return shortfall;
};

/** "1 midfielder", or "1 midfielder and 2 forwards" for a longer read. */
export const describeShortfall = (
  shortfall: readonly SquadShortfall[]
): string | undefined => {
  const clauses = shortfall.map(({ position, count }) => {
    const label = POSITION_LABELS[position];
    return `${count} ${count === 1 ? label.one : label.many}`;
  });

  if (clauses.length === 0) return undefined;
  if (clauses.length === 1) return clauses[0];
  return `${clauses.slice(0, -1).join(', ')} and ${
    clauses[clauses.length - 1]
  }`;
};

/** The position a screenshot read a player in, for naming a dropped one. */
export const describeMatchPosition = (match: ScreenshotMatch): string =>
  POSITION_LABELS[match.player.position].one;
